import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Notification from "../Notifications/Notification";
import { __ } from "@wordpress/i18n";
import MENU_CONFIG from "./MenuConfig";
import "./NavMenu.scss";


export default function NavMenu() {
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeSubMenus, setActiveSubMenus] = useState({});
  const [showNotifications, setShowNotifications] = useState(false);
  const location = useLocation();
  const menuTimeouts = useRef({});
  const menuRefs = useRef({});

  // Check if a path is active or contains the current location
  const isPathActive = (path) => {
    if (path === "/") {
      return location.pathname === "/" || location.pathname === "/settings";
    }
    
    // Check if current path starts with this path (for nested routes)
    return location.pathname === path || 
           (path !== "/" && location.pathname.startsWith(path + "/"));
  };

  // Check if any item in a menu is active
  const isMenuActive = (menuId) => {
    const menu = MENU_CONFIG[menuId];
    if (!menu) return false;
    
    return checkIfAnyItemActive(menu.items);
  };

  // Recursively check if any item in a nested structure is active
  const checkIfAnyItemActive = (items) => {
    if (!items || !items.length) return false;
    
    return items.some(item => 
      isPathActive(item.path) || checkIfAnyItemActive(item.items)
    );
  };

  // Get active status for a specific menu item
  const getMenuItemActiveStatus = (item) => {
    return isPathActive(item.path) || checkIfAnyItemActive(item.items);
  };

  // Handle mouse enter for main dropdown menu
  const handleMenuEnter = (menuId) => {
    // Clear any existing timeout
    if (menuTimeouts.current[menuId]) {
      clearTimeout(menuTimeouts.current[menuId]);
      menuTimeouts.current[menuId] = null;
    }
    
    setActiveMenu(menuId);
  };

  // Handle mouse leave for main dropdown with delay
  const handleMenuLeave = (menuId) => {
    menuTimeouts.current[menuId] = setTimeout(() => {
      setActiveMenu(null);
      setActiveSubMenus({});
    }, 300);
  };

  // Handle mouse enter for submenu
  const handleSubMenuEnter = (menuPath) => {
    // Clear any existing timeout for this submenu
    if (menuTimeouts.current[menuPath]) {
      clearTimeout(menuTimeouts.current[menuPath]);
      menuTimeouts.current[menuPath] = null;
    }
    
    setActiveSubMenus(prev => ({
      ...prev,
      [menuPath]: true
    }));
  };

  // Handle mouse leave for submenu
  const handleSubMenuLeave = (menuPath) => {
    menuTimeouts.current[menuPath] = setTimeout(() => {
      setActiveSubMenus(prev => {
        const newState = { ...prev };
        delete newState[menuPath];
        return newState;
      });
    }, 300);
  };

  // Toggle notifications panel
  const toggleNotifications = (e) => {
    e.preventDefault();
    setShowNotifications(!showNotifications);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!Object.values(menuRefs.current).some(ref => ref && ref.contains(event.target))) {
        setActiveMenu(null);
        setActiveSubMenus({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(menuTimeouts.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Reset active submenus when active menu changes
  useEffect(() => {
    if (!activeMenu) {
      setActiveSubMenus({});
    }
  }, [activeMenu]);

  // Recursively render menu items and their submenus
  const renderMenuItems = (items, level = 0, parentPath = '') => {
    if (!items || !items.length) return null;

    return items.map(item => {
      const fullPath = item.path;
      const itemId = parentPath ? `${parentPath}-${item.id}` : item.id;
      const hasSubItems = item.items && item.items.length > 0;
      const isActive = getMenuItemActiveStatus(item);
      const isSubMenuOpen = activeSubMenus[itemId] || isActive;

      return (
        <li key={itemId} className={`menu-item ${level > 0 ? 'submenu-item' : ''}`}>
          {hasSubItems ? (
            <div 
              className={`nested-dropdown ${isActive ? 'active' : ''}`}
              onMouseEnter={() => handleSubMenuEnter(itemId)}
              onMouseLeave={() => handleSubMenuLeave(itemId)}
            >
              <Link
                to={fullPath}
                className={`dropdown-item ${isActive ? 'active' : ''}`}
              >
                {__(item.label, "access-control")}
                <span className="submenu-indicator">›</span>
              </Link>
              
              {isSubMenuOpen && (
                <ul 
                  className="nested-dropdown-menu"
                  onMouseEnter={() => handleSubMenuEnter(itemId)}
                  onMouseLeave={() => handleSubMenuLeave(itemId)}
                >
                  {renderMenuItems(item.items, level + 1, itemId)}
                </ul>
              )}
            </div>
          ) : (
            <Link
              to={fullPath}
              className={`dropdown-item ${isActive ? 'active' : ''}`}
            >
              {__(item.label, "access-control")}
            </Link>
          )}
        </li>
      );
    });
  };

  return (
    <nav className="nav-menu">
      <div className="nav-wrapper">
        <div className="nav-left">
          {Object.entries(MENU_CONFIG).map(([menuId, menu]) => {
            const isActive = isMenuActive(menuId);
            const icon = isActive ? menu.activeIcon : menu.icon;
            
            return (
              <div
                key={menuId}
                ref={el => (menuRefs.current[menuId] = el)}
                className={`nav-dropdown ${isActive ? 'active' : ''}`}
                onMouseEnter={() => handleMenuEnter(menuId)}
                onMouseLeave={() => handleMenuLeave(menuId)}
              >
                <a
                  href="#"
                  className="nav-link"
                  onClick={(e) => e.preventDefault()}
                >
                  <img
                    src={`${window.placAdminData.pluginUrl}/assets/images/${icon}`}
                    alt={__(menuId, "access-control")}
                    className="nav-icon"
                  />
                  <span className="nav-text">
                    {__(menu.title, "access-control")}
                  </span>
                </a>
                
                <ul
                  className={`dropdown-menu ${activeMenu === menuId ? 'show' : ''}`}
                  onMouseEnter={() => handleMenuEnter(menuId)}
                  onMouseLeave={() => handleMenuLeave(menuId)}
                >
                  <li className="dropdown-header">
                    <span>{__(menu.description, "access-control")}</span>
                  </li>

                  {renderMenuItems(menu.items)}

                  <li className="dropdown-footer">
                    <div className="footer-content">
                      <div className="footer-text">
                        <h5>{__(menu.footer.title, "access-control")}</h5>
                        <span>{__(menu.footer.description, "access-control")}</span>
                      </div>
                      <Link to={menu.footer.action} className="footer-button">
                        {__(menu.footer.button, "access-control")}
                      </Link>
                    </div>
                  </li>
                </ul>
              </div>
            );
          })}
        </div>

        <div className="nav-right">
          <a href="#" className="nav-link">
            <img
              src={`${window.placAdminData.pluginUrl}/assets/images/translate.svg`}
              alt={__("translate", "access-control")}
              className="nav-icon"
            />
            <span className="nav-text">
              {__("Translate", "access-control")}
            </span>
          </a>

          <a href="#" className="nav-link">
            <img
              src={`${window.placAdminData.pluginUrl}/assets/images/feedback.svg`}
              alt={__("feedback", "access-control")}
              className="nav-icon"
            />
            <span className="nav-text">
              {__("Feedback", "access-control")}
            </span>
          </a>

          <a href="#" className="nav-link">
            <img
              src={`${window.placAdminData.pluginUrl}/assets/images/review.svg`}
              alt={__("review", "access-control")}
              className="nav-icon"
            />
            <span className="nav-text">
              {__("Review", "access-control")}
            </span>
          </a>

          <Link
            className={`nav-link ${isPathActive("/other-plugins") ? "active" : ""}`}
            to="/other-plugins"
          >
            <img
              src={`${window.placAdminData.pluginUrl}/assets/images/${
                isPathActive("/other-plugins") ? "other-active.svg" : "other.svg"
              }`}
              alt={__("other plugins", "access-control")}
              className="nav-icon"
            />
            <span className="nav-text">
              {__("Other Plugins", "access-control")}
            </span>
          </Link>

          <a
            href="#"
            onClick={toggleNotifications}
            className={`nav-link ${showNotifications ? "active" : ""}`}
          >
            <img
              src={`${window.placAdminData.pluginUrl}/assets/images/${
                showNotifications ? "notification-active.svg" : "notification.svg"
              }`}
              alt={__("notification", "access-control")}
              className="nav-icon"
            />
          </a>
          
          {showNotifications && (
            <Notification onClose={() => setShowNotifications(false)} />
          )}
        </div>
      </div>
    </nav>

  );
}